<template>
    <div
        class="heatmap-wrapper"
        :style="{ paddingLeft: '5px', paddingRight: normalizeSize(20, true) }"
        :class="{ 'has-scroll': activities.length > 10 }"
    >
        <div class="heatmap-container">
            <div class="heatmap-activities">
                <div v-for="(activity, aIndex) in activities" :key="aIndex" class="activity-row">
                    <span class="activity-name">{{ activity }}</span>
                </div>
            </div>
            <div class="heatmap-grid">
                <div class="heatmap-header">
                    <span v-for="hour in displayHours" :key="hour" class="hour-label"
                        >{{ hour }}{{ $t('statistics_v3.charts.hour_suffix') || 'h' }}</span
                    >
                </div>
                <div v-for="(row, rIndex) in heatmapData" :key="rIndex" class="heatmap-row">
                    <div
                        v-for="(cell, cIndex) in row"
                        :key="cIndex"
                        class="heatmap-cell"
                        :style="{ backgroundColor: getCellColor(cell) }"
                        :title="`${activities[rIndex]} - ${displayHours[cIndex]}${
                            $t('statistics_v3.charts.hour_suffix') || 'h'
                        }: ${cell} ${
                            parseInt(cell) > 1 ? $t('statistics_v3.charts.reservations') : $t('statistics_v3.charts.reservation')
                        }`"
                    ></div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import normalizeSize from '../../../../utils/normalizeSize'
export default {
    name: 'HeatmapChart',
    props: {
        data: {
            type: Array,
            required: true,
            // Expected format: 2D array [activities][hours] with values
        },
        activities: {
            type: Array,
            required: true,
        },
        startHour: {
            type: Number,
            default: 0,
        },
        endHour: {
            type: Number,
            default: 23,
        },
    },
    computed: {
        displayHours() {
            const hours = []
            for (let i = this.startHour; i <= this.endHour; i++) {
                hours.push(i)
            }
            return hours
        },
        heatmapData() {
            return this.data
        },
        maxValue() {
            let max = 0
            this.data.forEach((row) => {
                row.forEach((cell) => {
                    if (cell > max) max = cell
                })
            })
            return max || 1
        },
    },
    methods: {
        normalizeSize,
        getCellColor(value) {
            // Light color for cells with 0 reservations
            if (!value || value === 0) return '#6456FF'

            // Color scale from bright blue to dark (reversed scale)
            const colors = [
                'rgba(100, 80, 255, 0.8)', // Low value (>0)
                'rgba(74, 58, 255, 0.85)',
                'rgba(74, 58, 255, 0.7)',
                'rgba(74, 58, 255, 0.5)',
                'rgba(45, 40, 100, 0.9)',
                'rgba(30, 27, 57, 0.8)', // Dark
                'rgba(255, 255, 255, 0.05)', // Very dark / transparent (High value)
            ]

            let ratio = 0
            if (this.maxValue > 1) {
                ratio = (value - 1) / (this.maxValue - 1)
            } else {
                ratio = 1
            }

            const intensity = Math.min(Math.floor(ratio * (colors.length - 1)), colors.length - 1)
            return colors[intensity]
        },
    },
}
</script>

<style scoped>
.heatmap-wrapper {
    width: 100%;
}

.heatmap-wrapper.has-scroll {
    max-height: 400px;
    overflow-y: auto;
}

.heatmap-wrapper.has-scroll .heatmap-header {
    position: sticky;
    top: 0;
    background: linear-gradient(135deg, rgba(20, 25, 60, 0.95) 0%, rgba(15, 18, 45, 0.92) 100%);
    z-index: 1;
}

.heatmap-container {
    display: flex;
    gap: 10px;
    width: 100%;
}

.heatmap-activities {
    display: flex;
    flex-direction: column;
    gap: 0;
    padding-top: 30px;
    min-width: 150px;
}
@media (max-width: 768px) {
    .heatmap-activities {
        min-width: max-content;
        padding: 30px 10px;
    }
}
.activity-row {
    height: 32px;
    display: flex;
    align-items: center;
}

.activity-name {
    color: #fff;
    font-family: 'Poppins Regular', sans-serif;
    font-size: clamp(12px, 0.729545vw, 16px);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.heatmap-grid {
    flex: 1;
    overflow-x: auto;
}

.heatmap-header {
    display: flex;
    height: 30px;
    align-items: flex-end;
    padding-bottom: 5px;
}

.hour-label {
    min-width: 32px;
    flex: 1;
    color: #fff;
    font-family: Inter, sans-serif;
    font-size: 12px;
    font-weight: 400;
    text-align: center;
}

.heatmap-row {
    display: flex;
}

.heatmap-cell {
    min-width: 32px;
    flex: 1;
    height: 32px;
    cursor: pointer;
    transition: opacity 0.2s ease;
    border: none;
    border-bottom: 0.2px solid rgba(255, 255, 255, 0.08);
}

.heatmap-cell:hover {
    opacity: 0.8;
}
</style>
